import type { InfiniteData } from '@tanstack/react-query';

import type { Page } from '../api/pagination';
import type { NoteSummary } from '../notes/schema';
import type { NoteStatus } from '../notes/status';

/**
 * Patches one row's status in place across whichever already-fetched page holds it, without
 * touching page membership, order, or any stored cursor. Used after a successful retry
 * (`POST /notes/{id}/retry` already returns the note's new status, so there is no need to wait
 * for the next poll to show it) and is safe to call even while a note is on a page the caller
 * never fetched again: pages the id is not in are returned unchanged.
 */
export function withUpdatedNoteStatus(
  data: InfiniteData<Page<NoteSummary>> | undefined,
  noteId: string,
  status: NoteStatus,
): InfiniteData<Page<NoteSummary>> | undefined {
  if (data === undefined) {
    return data;
  }
  return {
    ...data,
    // A page with no matching row keeps its exact reference: only the one page (there is at
    // most one, ids are unique) that actually holds `noteId` gets a new object.
    pages: data.pages.map((page) => {
      if (!page.items.some((item) => item.id === noteId)) {
        return page;
      }
      return {
        ...page,
        items: page.items.map((item) =>
          item.id === noteId ? { ...item, status, failure_code: null } : item,
        ),
      };
    }),
  };
}

/**
 * Same patch as `withUpdatedNoteStatus`, but for the flat, single `Page` the `['notes',
 * 'counts']` query holds (it is a plain `useQuery`, not an infinite one). Without this, a
 * retry moves the row between the segmented control's sections in the list right away but
 * leaves the counts next to "Por hacer" / "En camino" / "En Excel" showing the pre-retry
 * numbers until the whole page reloads.
 */
export function withUpdatedNoteStatusInPage(
  data: Page<NoteSummary> | undefined,
  noteId: string,
  status: NoteStatus,
): Page<NoteSummary> | undefined {
  if (data === undefined || !data.items.some((item) => item.id === noteId)) {
    return data;
  }
  return {
    ...data,
    items: data.items.map((item) =>
      item.id === noteId ? { ...item, status, failure_code: null } : item,
    ),
  };
}
